import { useEffect, useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const teacherSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  subject: z.string().min(2, 'Subject is required'),
  experience: z.string().min(2, 'Experience is required'),
  photo_url: z.string().url('Must be a valid URL'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
});

export default function AdminTeachers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(teacherSchema),
  });

  useEffect(() => {
    fetchTeachers();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API}/teachers`);
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      if (editingTeacher) {
        await axios.put(`${API}/teachers/${editingTeacher.teacher_id}`, data, getAuthHeaders());
        toast.success('Teacher updated successfully');
      } else {
        await axios.post(`${API}/teachers`, data, getAuthHeaders());
        toast.success('Teacher added successfully');
      }
      fetchTeachers();
      setIsDialogOpen(false);
      reset();
      setEditingTeacher(null);
    } catch (error) {
      console.error('Error saving teacher:', error);
      toast.error(error.response?.data?.detail || 'Failed to save teacher');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (teacher) => {
    setEditingTeacher(teacher);
    setValue('name', teacher.name);
    setValue('subject', teacher.subject);
    setValue('experience', teacher.experience);
    setValue('photo_url', teacher.photo_url);
    setValue('description', teacher.description);
    setIsDialogOpen(true);
  };

  const handleDelete = async (teacherId) => {
    if (!window.confirm('Are you sure you want to delete this teacher?')) return;

    try {
      await axios.delete(`${API}/teachers/${teacherId}`, getAuthHeaders());
      toast.success('Teacher deleted successfully');
      fetchTeachers();
    } catch (error) {
      console.error('Error deleting teacher:', error);
      toast.error('Failed to delete teacher');
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingTeacher(null);
    reset();
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-brand-dark font-display">Manage Teachers</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-teacher-btn" onClick={() => { setEditingTeacher(null); reset(); setIsDialogOpen(true); }} className="bg-brand-blue hover:bg-blue-600">
              <Plus className="h-4 w-4 mr-2" />
              Add Teacher
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit(onSubmit)} data-testid="teacher-form" className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input id="name" data-testid="teacher-name-input" {...register('name')} placeholder="Teacher name" />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
              </div>

              <div>
                <Label htmlFor="subject">Subject *</Label>
                <Input id="subject" data-testid="teacher-subject-input" {...register('subject')} placeholder="Subject expertise" />
                {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject.message}</p>}
              </div>

              <div>
                <Label htmlFor="experience">Experience *</Label>
                <Input id="experience" data-testid="teacher-experience-input" {...register('experience')} placeholder="e.g., 10 years of teaching" />
                {errors.experience && <p className="text-red-500 text-sm mt-1">{errors.experience.message}</p>}
              </div>

              <div>
                <Label htmlFor="photo_url">Photo URL *</Label>
                <Input id="photo_url" data-testid="teacher-photo-input" {...register('photo_url')} placeholder="https://example.com/photo.jpg" />
                {errors.photo_url && <p className="text-red-500 text-sm mt-1">{errors.photo_url.message}</p>}
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea id="description" data-testid="teacher-description-input" {...register('description')} placeholder="Brief description about the teacher" rows={4} />
                {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>}
              </div>

              <Button type="submit" data-testid="teacher-submit-btn" disabled={isSubmitting} className="w-full">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : editingTeacher ? (
                  'Update Teacher'
                ) : (
                  'Add Teacher'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="inline-block h-8 w-8 animate-spin text-brand-blue" />
          <p className="mt-4 text-slate-600">Loading teachers...</p>
        </div>
      ) : teachers.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-600">No teachers yet. Add your first teacher!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teachers.map((teacher) => (
            <div key={teacher.teacher_id} data-testid={`teacher-item-${teacher.teacher_id}`} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <img src={teacher.photo_url} alt={teacher.name} className="w-24 h-24 rounded-full mx-auto mb-3 object-cover" />
              <h3 className="text-lg font-semibold text-brand-dark text-center">{teacher.name}</h3>
              <p className="text-brand-blue text-center font-medium mb-2">{teacher.subject}</p>
              <p className="text-sm text-slate-500 text-center mb-3">{teacher.experience}</p>
              <p className="text-sm text-slate-600 mb-4">{teacher.description}</p>
              <div className="flex gap-2">
                <Button
                  data-testid={`edit-teacher-${teacher.teacher_id}`}
                  onClick={() => handleEdit(teacher)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  data-testid={`delete-teacher-${teacher.teacher_id}`}
                  onClick={() => handleDelete(teacher.teacher_id)}
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
